import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthContext } from './hooks/useAuthContext';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './Firebase';
import { HelmetProvider } from 'react-helmet-async';
import React, { useEffect, useState } from 'react';
import CookieConsent from './components/CookieConsent';
import EmailLinkSignIn from './components/EmailLinkSignIn';

// pages & components
import GroupSearch from './pages/GroupSearch';
import RespondToSuggestion from './pages/RespondToSuggestion';
import RespondV4Preview from './pages/RespondV4Preview';
import RespondV5Preview from './pages/RespondV5Preview';
import RespondV6Preview from './pages/RespondV6Preview';
import RespondV7Preview from './pages/RespondV7Preview';
import ProfilePreview from './pages/ProfilePreview';
import MessengerIntro from './pages/MessengerIntro';
import MagicLogin from './pages/MagicLogin';
import Unsubscribe from './pages/Unsubscribe';
import Navbar from './components/Navbar';
import IndividualGroup from './pages/IndividualGroup';
import IndividualJoin from './pages/IndividualJoin';
import Login from './pages/Login';
import Home from './pages/Home';
import ProxyHome from './pages/ProxyHome';
import Test from './pages/Test';
import Tester1 from './pages/Tester1';
import ProfileUpdate from './pages/ProfileUpdate';
import Settings from './pages/Settings';
import Preferences from './pages/Preferences';
import NewCommunityEntry from './pages/NewCommunityEntry';
import Messenger from './pages/Messenger';
import ForgotPassword from "./pages/ForgotPassword";
import Onboarding from './pages/Onboarding';
import CommunityHome from './pages/CommunityHome';
import JoinAdditionalCommunity from './pages/JoinAdditionalCommunity';
import MoreInfo from './pages/MoreInfo';
import Demo from './pages/Demo';
import CommunityBuilder from './pages/CommunityBuilder';
import CreateAccount from './pages/CreateAccount';
import Admin from './pages/admin/Admin';
import CommunityManager from './pages/CommunityManager';
import EmailVerificationHandler from './pages/EmailVerificationHandler';
import VerifyDone from './pages/VerifyDone';
import ScrollToTop from './components/ScrollToTop';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import About from './pages/About';
import Contact from './pages/Contact';
import Safety from './pages/Safety';
import AIOnboarding from './pages/AIOnboarding';
import Footer from './components/Footer/Footer';


function AppContent() {
  const { user, setUser } = useAuthContext();
  const [fireBaseUser, setFireBaseUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showLoadingText, setShowLoadingText] = useState(false);
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  const [communityTag, setCommunityTag] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser !== fireBaseUser) {
        setFireBaseUser(currentUser);
      }
      setIsLoading(false);
    });
    return unsubscribe;
  }, [fireBaseUser, setUser]);

  // Safety net for the static hero shell in pre-built community pages:
  // CommunityHome removes it when it renders, but if the visitor ends up on
  // any other route first, it must not linger on top of that page.
  useEffect(() => {
    const shell = document.getElementById('static-shell');
    if (shell && shell.dataset.path?.toLowerCase() !== location.pathname.replace(/\/+$/, '').toLowerCase()) shell.remove();
  }, [location.pathname]);

  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'page_view', { page_path: location.pathname + location.search });
    }
    if (window.fbq) {
      window.fbq('track', 'PageView');
    }
  }, [location]);

  // Show loading text after 3 seconds if still loading
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isLoading) {
        setShowLoadingText(true);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [isLoading]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const isMessengerRoute = location.pathname.toLowerCase().includes('/messenger');
  const isDemoRoute = location.pathname.toLowerCase() === '/demo' || location.pathname.toLowerCase().startsWith('/meet/');
  
  // Get active component from URL hash or default to conversations
  const getActiveComponent = () => {
    const windowHash = window.location.hash.replace('#', '');
    const locationHash = location.hash.replace('#', '');
    console.log('[NAVBAR DEBUG] Window hash:', window.location.hash, 'Location hash:', location.hash);
    console.log('[NAVBAR DEBUG] Processed window hash:', windowHash, 'Processed location hash:', locationHash);
    return windowHash || locationHash || 'messenger-conversations';
  };
  
  const [activeComponent, setActiveComponent] = useState(getActiveComponent());
  
  // Listen for hash changes to track active component
  useEffect(() => {
    const handleHashChange = () => {
      const newComponent = getActiveComponent();
      console.log('[NAVBAR DEBUG] Hash changed, new component:', newComponent);
      setActiveComponent(newComponent);
    };
    
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Sync activeComponent with location changes (including initial load)
  useEffect(() => {
    const newComponent = getActiveComponent();
    console.log('[NAVBAR DEBUG] Location changed, updating activeComponent to:', newComponent);
    setActiveComponent(newComponent);
  }, [location.hash]);
  
  // Check URL parameters for onboarding/additional-community flows
  const urlParams = new URLSearchParams(location.search);
  const fromOnboarding = urlParams.get('from') === 'onboarding';
  const fromAdditionalCommunity = urlParams.get('from') === 'additional-community';
  
  // Define auth routes that should hide navbar (minimal auth flows only)
  const isAuthRoute = location.pathname.toLowerCase().includes('/forgot-password') ||
                     location.pathname.toLowerCase().includes('/verify-email');
  
  // Hide navbar when:
  // 1. Mobile: Hide when NOT in conversations (i.e., hide in chatbox, profile, suggestion-profile)
  // 2. Desktop: Always show navbar (never hide on desktop)  
  // 3. Coming from onboarding or additional-community flows (all devices)
  // 4. On demo route (all devices)
  // 5. On login, signup, forgot password, verify email, onboarding pages (all devices)
  const isProfileOrSettingsRoute = location.pathname.toLowerCase().includes('/profileupdate') ||
                                   location.pathname.toLowerCase().includes('/settings');
  
  const shouldHideNavbar = isDemoRoute || isAuthRoute || (isMessengerRoute && isMobile && (
    activeComponent !== 'messenger-conversations' ||
    fromOnboarding ||
    fromAdditionalCommunity
  ));

  // Debug logging for navbar visibility
  if (isMessengerRoute) {
    console.log('[NAVBAR DEBUG] URL:', location.pathname + location.search + location.hash);
    console.log('[NAVBAR DEBUG] activeComponent:', activeComponent);
    console.log('[NAVBAR DEBUG] fromOnboarding:', fromOnboarding);
    console.log('[NAVBAR DEBUG] isMobile:', isMobile);
    console.log('[NAVBAR DEBUG] shouldHideNavbar:', shouldHideNavbar);
  }


  // Define routes where footer should be hidden
  const isFooterHiddenRoute = location.pathname.toLowerCase().includes('/login') || 
                             location.pathname.toLowerCase().includes('/signup');
  
  // Hide footer for auth flows, messenger, demo routes, profile and settings pages to improve UX
  const shouldHideFooter = isDemoRoute || isMessengerRoute || isAuthRoute || isProfileOrSettingsRoute || isFooterHiddenRoute;

  // Check if we're on a community page (route is /:communityName) or Home page
  // Exclude known non-community routes
  const knownRoutes = ['/groupsearch', '/moreInfo', '/login', '/create-account', '/forgot-password', '/profileupdate', '/settings', '/preferences', '/messenger', '/onboarding', '/newcommunityentry', '/create', '/community-manager', '/demo', '/terms', '/privacy', '/about', '/contact', '/safety', '/verify-done', '/admin', '/respond', '/respond-v4-preview', '/respond-v5-preview', '/respond-v6-preview', '/respond-v7-preview', '/profilepreview', '/unsubscribe', '/meet/'];
  const isCommunityPage = location.pathname !== '/' && 
                          !knownRoutes.some(route => location.pathname.toLowerCase().startsWith(route)) &&
                          !location.pathname.startsWith('/groupsearch/') &&
                          !location.pathname.startsWith('/JoinAdditionalCommunity/');
  const isHomePage = location.pathname === '/';

  // /respond and /ProfilePreview wash their background to a fixed, fairly
  // light overlay (see RespondToSuggestion.css) regardless of the
  // underlying community photo, so the transparent navbar's default white
  // text never has reliable contrast there — force dark text specifically
  // on these two routes rather than on every community page (whose real
  // hero photos are often dark and rely on the white text).
  const isPublicTokenRoute = location.pathname.toLowerCase() === '/respond' ||
                             location.pathname.toLowerCase() === '/respond-v4-preview' ||
                             location.pathname.toLowerCase() === '/respond-v5-preview' ||
                             location.pathname.toLowerCase() === '/respond-v6-preview' ||
                             location.pathname.toLowerCase() === '/respond-v7-preview' ||
                             location.pathname.toLowerCase() === '/messenger/intro' ||
                             location.pathname.toLowerCase() === '/profilepreview';

  // When user is logged in and on home page, they see LoggedInHome dashboard - use black navbar
  // When user is not logged in and on home page, they see marketing homepage - use transparent navbar
  const shouldUseTransparentNavbar = isCommunityPage || (isHomePage && !user);
  const shouldUseBlackNavbar = !shouldUseTransparentNavbar;

  const navbarProps = shouldUseTransparentNavbar ? {
    isTransparent: true,
    hideTitle: false,
    fadeOnScroll: true,
    communityTag: isHomePage ? communityTag : null,
    forceDarkText: isPublicTokenRoute
  } : shouldUseBlackNavbar ? {
    isBlack: true
  } : {};


  if (isLoading) {
    return <div>{showLoadingText ? 'Loading...' : ''}</div>;
  }

  return (
    <div className="App">
      <ScrollToTop />
      <Navbar {...navbarProps} shouldHide={shouldHideNavbar} />
      <div className="pages">
          <Routes>
            <Route path="/Friends_Buddies_Finder_Brighton" element={<Navigate to="/Brighton_People" replace />} />
            <Route path="/" element={<Home setCommunityTag={setCommunityTag} />} />
            <Route path="/demo" element={<Demo />} />
            <Route path="/meet/:communityRef" element={<AIOnboarding />} />
            <Route path="/respond" element={<RespondToSuggestion />} />
            <Route path="/respond-v4-preview" element={<RespondV4Preview />} />
            <Route path="/respond-v5-preview" element={<RespondV5Preview />} />
            <Route path="/respond-v6-preview" element={<RespondV6Preview />} />
            <Route path="/respond-v7-preview" element={<RespondV7Preview />} />
            <Route path="/messenger/intro" element={<MessengerIntro />} />
            <Route path="/magic" element={<MagicLogin />} />
            <Route path="/ProfilePreview" element={<ProfilePreview />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/:communityName" element={<CommunityHome />} />
            <Route path="/moreInfo" element={<MoreInfo />} />
            <Route path="/groupsearch" element={<GroupSearch />} />
            <Route path="/groupsearch/group/:groupID" element={<IndividualGroup />} />
            <Route path="/login" element={<Login />} />
            <Route path="/create-account" element={<CreateAccount />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/verify-email" element={<EmailVerificationHandler />} />
            <Route path="/verify-done" element={<VerifyDone />} />
            <Route path="/groupsearch/join/:groupID" element={<IndividualJoin />} />
            <Route path="/groupsearch/group/proxy/:groupID" element={<ProxyHome />} />
            <Route path="/test" element={<Test />} />
            <Route
              path="/ProfileUpdate"
              element={
                fireBaseUser ? (
                  <ProfileUpdate />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/Onboarding"
              element={
                fireBaseUser ? (
                  <Onboarding />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/JoinAdditionalCommunity/:communityId"
              element={
                fireBaseUser ? (
                  <JoinAdditionalCommunity />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/tester1"
              element={
                fireBaseUser ? (
                  <Tester1 />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/messenger"
              element={
                fireBaseUser ? (
                  <Messenger />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/settings"
              element={
                fireBaseUser ? (
                  <Settings />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/preferences"
              element={
                fireBaseUser ? (
                  <Preferences />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route path="/newcommunityentry" element={<Navigate to="/create" replace />} />
            <Route path="/create" element={<CommunityBuilder />} />
            <Route path="/community-manager" element={<CommunityManager />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/safety" element={<Safety />} />
            <Route
              path="/Messenger"
              element={
                fireBaseUser ? (
                  <Messenger />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
          </Routes>
        </div>
      {!shouldHideFooter && <Footer />}
      <CookieConsent />
      <EmailLinkSignIn />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
// import { BrowserRouter, Routes, Route, Navigate, } from 'react-router-dom';
// import { useAuthContext } from './hooks/useAuthContext';
// import { onAuthStateChanged } from 'firebase/auth';
// import { auth } from './/Firebase'
// import React, { useEffect, useState } from 'react'; // Import useEffect and useState from 'react'

// // pages & components
// import GroupSearch from './pages/GroupSearch';
// import Navbar from './components/Navbar';
// import IndividualGroup from './pages/IndividualGroup';
// import IndividualJoin from './pages/IndividualJoin';
// import Login from './pages/Login';
// import Home from './pages/Home';
// import ProxyHome from './pages/ProxyHome';
// import Test from './pages/Test';
// import Tester1 from './pages/Tester1';
// import ProfileUpdate from './pages/ProfileUpdate';
// import Settings from './pages/Settings';
// import Preferences from './pages/Preferences';
// import NewCommunityEntry from './pages/NewCommunityEntry';
// import Messenger from './pages/Messenger';
// import ForgotPassword from "./pages/ForgotPassword"; // Import ForgotPasswordForm
// import Onboarding from './pages/Onboarding';
// import CommunityHome from './pages/CommunityHome';
// import JoinAdditionalCommunity from './pages/JoinAdditionalCommunity';
// import MoreInfo from './pages/MoreInfo';
// import ScrollToTop from './components/ScrollToTop';

// function App() {
//   const { setUser } = useAuthContext();
//   const [fireBaseUser, setFireBaseUser] = useState(null);
//   const [isLoading, setIsLoading] = useState(true); // Add this line

//   // Use the useEffect hook to set up the onAuthStateChanged observer
//   useEffect(() => {
//     const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
//       // Check if the user state has changed before updating
//       if (currentUser !== fireBaseUser) {
//         setFireBaseUser(currentUser);
//       }
//       setIsLoading(false); // Add this line
//     });

//     // Clean up the observer on component unmount
//     return unsubscribe;
//   }, [fireBaseUser, setUser]);

//   if (isLoading) { // Add this block
//     return <div>Loading...</div>;
//   }


//   return (
//     <div className="App">
//       <BrowserRouter>
//         <Navbar />
//         <div className="pages">
//           <Routes>
//             <Route path="/" element={<Home />} />
//             <Route path="/:communityName" element={<CommunityHome />} />
//             <Route path="/moreInfo" element={<MoreInfo />} />
//             <Route path="/groupsearch" element={<GroupSearch />} />
//             <Route path="/groupsearch/group/:groupID" element={<IndividualGroup />} />
//             <Route path="/login" element={<Login />} />
//             <Route path="/forgotpassword" element={<ForgotPassword />} /> {/* Add this line */}
//             <Route path="/groupsearch/join/:groupID" element={<IndividualJoin />} />
//             <Route path="/groupsearch/group/proxy/:groupID" element={<ProxyHome />} />
//             <Route path="/test" element={<Test />} />
//             <Route
//               path="/ProfileUpdate"
//               element={
//                 fireBaseUser ? (
//                   <ProfileUpdate />
//                 ) : (
//                   <Navigate to="/login" replace />
//                 )
//               }
//             />
//             <Route
//               path="/Onboarding"
//               element={
//                 fireBaseUser ? (
//                   <Onboarding />
//                 ) : (
//                   <Navigate to="/login" replace />
//                 )
//               }
//             />
//              <Route
//               path="/JoinAdditionalCommunity/:communityId"
//               element={
//                 fireBaseUser ? (
//                   <JoinAdditionalCommunity />
//                 ) : (
//                   <Navigate to="/login" replace />
//                 )
//               }
//             />
//             <Route
//               path="/tester1"
//               element={
//                 fireBaseUser ? (
//                   <Tester1 />
//                 ) : (
//                   <Navigate to="/login" replace />
//                 )
//               }
//             />
//             <Route
//               path="/messenger"
//               element={
//                 fireBaseUser ? (
//                   <Messenger />
//                 ) : (
//                   <Navigate to="/login" replace />
//                 )
//               }
//             />
//             <Route
//               path="/settings"
//               element={
//                 fireBaseUser ? (
//                   <Settings />
//                 ) : (
//                   <Navigate to="/login" replace />
//                 )
//               }
//             />
//             <Route
//               path="/preferences"
//               element={
//                 fireBaseUser ? (
//                   <Preferences />
//                 ) : (
//                   <Navigate to="/login" replace />
//                 )
//               }
//             />
//             <Route path="/newcommunityentry" element={<NewCommunityEntry />} />
//             <Route
//               path="/Messenger"
//               element={
//                 fireBaseUser ? (
//                   <Messenger />
//                 ) : (
//                   <Navigate to="/login" replace />
//                 )
//               }
//             />
//           </Routes>
//         </div>
//       </BrowserRouter>
//     </div>
//   );
// }

// export default App;